import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowDown,
  ArrowUp,
  FilmReel,
  Image as ImageIcon,
  Plus,
  Prohibit,
  Trash,
  X,
} from '@phosphor-icons/react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import {
  BulletList,
  EditorForm,
  Field,
  inputClasses,
  textareaClasses,
  type EditorSectionProps,
} from './shared';
import { projectsSchema, type ProjectsValues, MEDIA_KINDS } from '../schemas';
import { MediaInput } from '../components/MediaInput';

const MEDIA_OPTIONS: Record<
  (typeof MEDIA_KINDS)[number],
  { label: string; icon: typeof Prohibit; hint: string }
> = {
  none: { label: 'None', icon: Prohibit, hint: 'Text only' },
  image: { label: 'Image', icon: ImageIcon, hint: 'PNG, JPG, WebP' },
  video: { label: 'Video', icon: FilmReel, hint: 'MP4 or YouTube/Vimeo' },
};

export const PROJECTS_SEED: ProjectsValues = {
  items: [],
};

export function ProjectsEditor({
  initial,
  onDirty,
  onSubmit,
  submitting,
  onDraftChange,
}: EditorSectionProps<ProjectsValues>) {
  const form = useForm<ProjectsValues>({
    resolver: zodResolver(projectsSchema) as Resolver<ProjectsValues>,
    defaultValues: initial ?? PROJECTS_SEED,
    mode: 'onBlur',
  });
  const { fields, append, remove, move } = useFieldArray({ control: form.control, name: 'items' });
  const [openId, setOpenId] = useState<string | null>(fields[0]?.id ?? null);

  useEffect(() => {
    onDraftChange?.(form.getValues());
    const sub = form.watch((v) => {
      onDirty();
      onDraftChange?.(v);
    });
    return () => sub.unsubscribe();
  }, [form, onDirty, onDraftChange]);

  const { errors } = form.formState;

  return (
    <EditorForm onSubmit={form.handleSubmit(async (v) => onSubmit(v))} submitting={submitting}>
      <div className="space-y-3">
        {fields.map((f, idx) => {
          const isOpen = f.id === openId;
          const item = form.watch(`items.${idx}`);
          const rowErrors = errors.items?.[idx];
          const mediaKind = item?.mediaKind ?? 'none';
          return (
            <div key={f.id} className="overflow-hidden rounded-xl border border-line bg-board">
              <div className="flex w-full items-center gap-1 px-2 py-2">
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => idx > 0 && move(idx, idx - 1)}
                    disabled={idx === 0}
                    className="rounded p-0.5 text-ink-faint hover:text-ink disabled:opacity-20"
                    aria-label="Move up"
                  >
                    <ArrowUp size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => idx < fields.length - 1 && move(idx, idx + 1)}
                    disabled={idx === fields.length - 1}
                    className="rounded p-0.5 text-ink-faint hover:text-ink disabled:opacity-20"
                    aria-label="Move down"
                  >
                    <ArrowDown size={13} />
                  </button>
                </div>
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : f.id)}
                className="flex flex-1 items-center gap-3 px-2 py-1 text-left transition-colors hover:bg-rail/60 rounded-lg"
              >
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink">
                    {item?.title || 'Untitled'}
                  </p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink-faint">
                    {item?.year}
                    {item?.role ? ` · ${item.role}` : ''}
                    {item?.mediaKind && item.mediaKind !== 'none' ? ` · ${item.mediaKind}` : ''}
                    {item?.url ? ' · linked' : ''}
                  </p>
                </div>
                <span className="font-mono text-[11px] uppercase text-ink-faint">
                  {isOpen ? 'Close' : 'Edit'}
                </span>
              </button>
              </div>
              {isOpen ? (
                <div className="space-y-4 border-t border-line bg-canvas p-5">
                  <Field label="Title" error={rowErrors?.title?.message}>
                    <input
                      {...form.register(`items.${idx}.title`)}
                      placeholder="Project name"
                      className={inputClasses}
                    />
                  </Field>
                  <Field label="Summary" error={rowErrors?.summary?.message}>
                    <textarea
                      {...form.register(`items.${idx}.summary`)}
                      placeholder="One or two sentences describing the work."
                      className={textareaClasses}
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Role" hint="Your role on the project." error={rowErrors?.role?.message}>
                      <input
                        {...form.register(`items.${idx}.role`)}
                        placeholder="Design lead"
                        className={inputClasses}
                      />
                    </Field>
                    <Field label="Client" hint="Optional." error={rowErrors?.client?.message}>
                      <input
                        {...form.register(`items.${idx}.client`)}
                        placeholder="Studio name"
                        className={inputClasses}
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Year" error={rowErrors?.year?.message}>
                      <input {...form.register(`items.${idx}.year`)} className={inputClasses} />
                    </Field>
                    <Field label="Project URL" error={rowErrors?.url?.message}>
                      <input
                        {...form.register(`items.${idx}.url`)}
                        placeholder="https://"
                        className={inputClasses}
                      />
                    </Field>
                  </div>

                  <Field label="Cover media">
                    <Controller
                      control={form.control}
                      name={`items.${idx}.mediaKind`}
                      render={({ field }) => (
                        <RadioGroup.Root
                          value={field.value}
                          onValueChange={field.onChange}
                          className="grid grid-cols-3 gap-2"
                        >
                          {MEDIA_KINDS.map((kind) => {
                            const meta = MEDIA_OPTIONS[kind];
                            const Icon = meta.icon;
                            return (
                              <RadioGroup.Item
                                key={kind}
                                value={kind}
                                className="flex items-center gap-2 rounded-lg border border-line bg-board px-3 py-2.5 text-left transition-colors data-[state=checked]:border-ink data-[state=checked]:bg-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/20"
                              >
                                <Icon size={16} className="text-ink-muted" />
                                <span>
                                  <span className="block text-sm font-medium text-ink">
                                    {meta.label}
                                  </span>
                                  <span className="block text-[11px] text-ink-faint">
                                    {meta.hint}
                                  </span>
                                </span>
                              </RadioGroup.Item>
                            );
                          })}
                        </RadioGroup.Root>
                      )}
                    />
                  </Field>

                  {mediaKind !== 'none' ? (
                    <div className="space-y-4 rounded-lg border border-line bg-board p-4">
                      <Field
                        label={mediaKind === 'image' ? 'Image' : 'Video'}
                        error={rowErrors?.mediaUrl?.message}
                      >
                        <Controller
                          control={form.control}
                          name={`items.${idx}.mediaUrl`}
                          render={({ field }) => (
                            <MediaInput
                              kind={mediaKind}
                              value={field.value ?? ''}
                              onChange={field.onChange}
                              purpose="project-cover"
                            />
                          )}
                        />
                      </Field>
                      {mediaKind === 'image' ? (
                        <Field
                          label="Alt text"
                          hint="Accessibility description."
                          error={rowErrors?.mediaAlt?.message}
                        >
                          <input
                            {...form.register(`items.${idx}.mediaAlt`)}
                            placeholder="What's in the image?"
                            className={inputClasses}
                          />
                        </Field>
                      ) : null}
                    </div>
                  ) : null}

                  <Field label="Bullet points" hint="Short bullets — what you did, what shipped. Max 8.">
                    <Controller
                      control={form.control}
                      name={`items.${idx}.bullets`}
                      render={({ field }) => (
                        <BulletList value={field.value ?? []} onChange={field.onChange} />
                      )}
                    />
                  </Field>

                  <Field label="Tags" hint="Press enter to add.">
                    <Controller
                      control={form.control}
                      name={`items.${idx}.tags`}
                      render={({ field }) => (
                        <TagInput
                          value={field.value ?? []}
                          onChange={field.onChange}
                          placeholder="Add a tag"
                        />
                      )}
                    />
                  </Field>

                  <div className="flex justify-end border-t border-line pt-4">
                    <button
                      type="button"
                      onClick={() => remove(idx)}
                      className="flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium text-ink-muted transition-colors hover:border-red-300 hover:text-red-700"
                    >
                      <Trash size={12} /> Remove project
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => {
          const id = Math.random().toString(36).slice(2, 8);
          append({
            id,
            title: '',
            summary: '',
            role: '',
            client: '',
            year: String(new Date().getFullYear()),
            url: '',
            mediaKind: 'none',
            mediaUrl: '',
            mediaAlt: '',
            tags: [],
            bullets: [],
          });
          setOpenId(id);
        }}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-board py-4 text-sm font-medium text-ink-muted transition-colors hover:border-ink hover:text-ink"
      >
        <Plus size={14} /> Add project
      </button>
    </EditorForm>
  );
}

function TagInput({
  value,
  onChange,
  placeholder,
}: {
  value: ReadonlyArray<string>;
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  return (
    <div className="flex flex-wrap gap-2 rounded-md border border-line bg-board p-2">
      {value.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1.5 rounded-full border border-line bg-rail px-2.5 py-1 text-xs text-ink"
        >
          {tag}
          <button
            type="button"
            onClick={() => onChange(value.filter((t) => t !== tag))}
            aria-label={`Remove ${tag}`}
            className="text-ink-faint hover:text-ink"
          >
            <X size={10} weight="bold" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          const next = draft.trim();
          if (!next || value.includes(next)) return;
          onChange([...value, next]);
          setDraft('');
        }}
        placeholder={placeholder}
        className="min-w-[120px] flex-1 bg-transparent px-2 py-1 text-sm text-ink placeholder:text-ink-faint focus:outline-none"
      />
    </div>
  );
}
